import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  AuthFlowType,
  SignUpCommand,
  ConfirmSignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider'

import { Interceptor } from '@connectrpc/connect'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { useCallback, useMemo } from 'react'
import { useBackendApiCreateUser } from './backend-api/user'
import { Result } from 'result-type-ts'
import { uuidv7 } from 'uuidv7'

type AwsCognitoConfig = {
  region: string
  userPoolId: string
  clientId: string
}
declare let IRDB_AWS_REGION: string | undefined
if (!IRDB_AWS_REGION) {
  throw new Error('The IRDB_AWS_REGION environment variable is not set')
}
declare let IRDB_AWS_COGNITO_USERPOOL_ID: string | undefined
if (!IRDB_AWS_COGNITO_USERPOOL_ID) {
  throw new Error('The IRDB_AWS_COGNITO_USERPOOL_ID environment variable is not set')
}
declare let IRDB_AWS_COGNITO_CLIENT_ID: string | undefined
if (!IRDB_AWS_COGNITO_CLIENT_ID) {
  throw new Error('The IRDB_AWS_COGNITO_CLIENT_ID environment variable is not set')
}
const config: AwsCognitoConfig = {
  region: IRDB_AWS_REGION,
  userPoolId: IRDB_AWS_COGNITO_USERPOOL_ID,
  clientId: IRDB_AWS_COGNITO_CLIENT_ID,
}

export const cognitoClient = new CognitoIdentityProviderClient({
  region: config.region,
})
const useSignIn = () => {
  const { mutateAsync } = useBackendApiCreateUser()
  const signIn = useCallback(
    async (username: string, password: string): Promise<Result<void>> => {
      const params = {
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: config.clientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      }
      try {
        const command = new InitiateAuthCommand(params)
        const { AuthenticationResult } = await cognitoClient.send(command)
        if (AuthenticationResult) {
          localStorage.setItem('idToken', AuthenticationResult.IdToken || '')
          localStorage.setItem('accessToken', AuthenticationResult.AccessToken || '')
          localStorage.setItem('refreshToken', AuthenticationResult.RefreshToken || '')
          const decodedAccessToken = jwtDecode<JwtPayload & { username?: string }>(AuthenticationResult.AccessToken || '')
          const decodedIdToken = jwtDecode<JwtPayload & { email?: string }>(AuthenticationResult.IdToken || '')
          if (decodedAccessToken.username && decodedAccessToken.sub && decodedIdToken.email) {
            const createUserResult = await mutateAsync({
              user: {
                email: decodedIdToken.email || '',
                cognitoUserId: decodedAccessToken.sub,
                name: decodedAccessToken.username,
                createdAt: new Date().toISOString(),
              },
            })
            console.log('Create user result: ', createUserResult)
          }
          return Result.success(undefined)
        }
        return Result.success(undefined)
      } catch (error) {
        console.error('Error signing in: ', error)
        return Result.failure(error)
      }
    },
    [mutateAsync],
  )
  return { signIn }
}
const useSignup = () => {
  const { mutateAsync } = useBackendApiCreateUser()
  const signUp = useCallback(
    async (email: string, password: string): Promise<Result<void>> => {
      const regex = /(.*)(?=@)/g
      const results = email.match(regex)
      if (results === null || results.length === 0) {
        console.error('Invalid email format')
        return Result.failure(new Error('Invalid email format'))
      }
      const username = results[0]
      const params = {
        ClientId: config.clientId,
        Username: username, // FIXME: cognitoのusernameをとる
        Password: password,
        UserAttributes: [
          {
            Name: 'email',
            Value: email,
          },
        ],
      }
      try {
        const command = new SignUpCommand(params)
        const response = await cognitoClient.send(command)
        if (!response.UserSub) {
          throw new Error('UserSub is not defined')
        }
        const createUserResult = await mutateAsync({
          user: {
            id: uuidv7(),
            email,
            cognitoUserId: response.UserSub,
            name: username,
            createdAt: new Date().toISOString(),
          },
        })
        if (createUserResult.error) {
          console.error('Error createUser: ', createUserResult)
          return Result.failure(createUserResult.error)
        }

        console.log('Sign up success: ', response)
        return Result.success(undefined)
      } catch (error) {
        console.error('Error signing up: ', error)
        return Result.failure(error)
      }
    },
    [mutateAsync],
  )
  return { signUp }
}
const useConfirmSignUp = () => {
  const confirmSignUp = useCallback(async (email: string, code: string): Promise<Result<void>> => {
    const regex = /(.*)(?=@)/g
    const results = email.match(regex)
    if (results === null || results.length === 0) {
      console.error('Invalid email format')
      return Result.failure(new Error('Invalid email format'))
    }
    const username = results[0]
    const params = {
      ClientId: config.clientId,
      Username: username,
      ConfirmationCode: code,
    }
    try {
      const command = new ConfirmSignUpCommand(params)
      await cognitoClient.send(command)
      console.log('User confirmed successfully')
      return Result.success(undefined)
    } catch (error) {
      console.error('Error confirming sign up: ', error)
      return Result.failure(error)
    }
  }, [])
  return { confirmSignUp }
}
export const authenticator: Interceptor = (next) => async (req) => {
  const accessToken = localStorage.getItem('accessToken')
  if (accessToken !== null) {
    req.header.set('Authorization', 'Bearer ' + accessToken)
  }
  return await next(req)
}

export type Auth = (
  | {
      isAuthenticated: true
      username: string
      cognitoUserId: string
      logout: () => void
    }
  | {
      isAuthenticated: false
    }
) & {
  signIn: ReturnType<typeof useSignIn>['signIn']
  signUp: ReturnType<typeof useSignup>['signUp']
  confirmSignUp: ReturnType<typeof useConfirmSignUp>['confirmSignUp']
}
export const useAuth = (): Auth => {
  const idToken = localStorage.getItem('idToken')
  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const logout = useCallback(() => {
    localStorage.removeItem('idToken')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }, [])
  const { signIn } = useSignIn()
  const { signUp } = useSignup()
  const { confirmSignUp } = useConfirmSignUp()
  const auth = useMemo<Auth>(() => {
    const isAuthenticated = idToken !== null && accessToken !== null && refreshToken !== null
    if (!isAuthenticated) {
      return { isAuthenticated, signIn, signUp, confirmSignUp }
    }
    const decoded = jwtDecode<JwtPayload & { username?: string }>(accessToken || '')
    if (!decoded.username || !decoded.sub) {
      return { isAuthenticated: false, signIn, signUp, confirmSignUp }
    }
    return {
      isAuthenticated: true,
      username: decoded.username,
      cognitoUserId: decoded.sub,
      logout,
      signIn,
      signUp,
      confirmSignUp,
    }
  }, [idToken, accessToken, refreshToken, logout, signIn, signUp, confirmSignUp])
  return auth
}
